import { constants } from '@bedc/constants';
import type { types } from '@bedc/types';

import { conversionUtils } from './conversionUtils';
import { geometryUtils } from './geometryUtils';

export type ThicknessOption = {
	label: string;
	value: number;
};

type FloorLevelState = {
	floorLevel?: string | null;
	page: {
		pageNumber: number;
	};
};

type AutoCompletableStepState = {
	floorLevel?: types.ai.floorLevel.Id;
	isDiscarded?: boolean;
	discardedAt?: string | null;
	frames: types.job.hitlv2.frame.Type[];
	lengthCorrectionPrompts?: types.job.hitlv2.length.LengthCorrectionPrompt[];
	externalDimensions?: types.job.hitlv2.ExternalDimensions | null;
};

type HITLV2StateDiscardStatus = Pick<AutoCompletableStepState, 'isDiscarded' | 'discardedAt'>;

export const isActiveHITLV2State = (state: HITLV2StateDiscardStatus): boolean =>
	!state.isDiscarded && !state.discardedAt;

const hasLengthReviewPage = (state: AutoCompletableStepState): boolean =>
	isActiveHITLV2State(state) &&
	Boolean(state.floorLevel) &&
	state.floorLevel !== constants.ai.floorLevel.notApplicable;

/**
 * Whether Brain Engine measured this level's overall external size.
 *
 * The External Dimensions step exists to correct that measurement, so a level
 * without one has nothing to ask and the step can complete on its own.
 */
const hasExternalDimensions = (state: AutoCompletableStepState): boolean =>
	isActiveHITLV2State(state) &&
	Boolean(state.floorLevel) &&
	state.floorLevel !== constants.ai.floorLevel.notApplicable &&
	Boolean(state.externalDimensions);

export const isHITLV2StepAutoCompletable = (
	stepId: types.job.hitlv2.step.Id
): stepId is 'length' | 'externalDimensions' =>
	stepId === 'length' || stepId === 'externalDimensions';

/**
 * Whether an auto-completable correction step has anything the estimator can
 * act on. Other steps intentionally return true because missing values in
 * those steps can themselves require correction.
 */
export const doesHITLV2StepRequireUserInput = (
	stepId: types.job.hitlv2.step.Id,
	states: AutoCompletableStepState[]
): boolean => {
	switch (stepId) {
		case 'length':
			return states.some(hasLengthReviewPage);
		case 'externalDimensions':
			return states.some(hasExternalDimensions);
		default:
			return true;
	}
};

const getThicknessValues = (
	measurementSystem: types.ui.MeasurementSystem,
	cmu = false
): readonly number[] =>
	cmu
		? constants.job.hitlv2.thickness.cmuValues[measurementSystem]
		: constants.job.hitlv2.thickness.values[measurementSystem];

const getDefaultSegmentThickness = (
	measurementSystem: types.ui.MeasurementSystem,
	cmu = false
): number =>
	cmu
		? constants.job.hitlv2.segments.defaultCmuThickness[measurementSystem]
		: constants.job.hitlv2.segments.defaultThickness[measurementSystem];

export const getHitlv2FloorLevelSortValue = (floorLevel?: string | null): number => {
	if (!floorLevel) return Infinity;

	// Canonical IDs (`level0`..`level7`): use the numeric suffix as the rank.
	// `level0` is Basement and must sort first.
	const idMatch = floorLevel.match(/^level(\d+)$/i);
	if (idMatch) return Number(idMatch[1]);

	// Display labels: `Level N` → N. `Basement` is the conventional label for
	// `level0` and must sort first regardless of selection order.
	const labelMatch = floorLevel.match(/^Level\s+(\d+)$/i);
	if (labelMatch) return Number(labelMatch[1]);

	if (/^basement$/i.test(floorLevel)) return 0;

	return Infinity;
};

export const compareHitlv2StatesByFloorLevel = <T extends FloorLevelState>(
	left: T,
	right: T
): number => {
	const leftLevel = getHitlv2FloorLevelSortValue(left.floorLevel);
	const rightLevel = getHitlv2FloorLevelSortValue(right.floorLevel);

	if (leftLevel !== rightLevel) {
		return leftLevel - rightLevel;
	}

	return left.page.pageNumber - right.page.pageNumber;
};

export const normalizeHitlv2ThicknessFromPageScale = (
	thickness: number | null | undefined,
	pageScale: number | null | undefined
): number | null => {
	if (typeof thickness !== 'number' || !Number.isFinite(thickness)) {
		return null;
	}

	if (typeof pageScale !== 'number' || !Number.isFinite(pageScale) || pageScale <= 0) {
		return thickness;
	}

	return thickness * pageScale;
};

export const normalizeThicknessOptionValue = (
	value: number,
	measurementSystem: types.ui.MeasurementSystem
) => (measurementSystem === 'imperial' ? Math.round(value * 16) / 16 : Math.round(value));

export const getThicknessOptionKey = (
	value: number,
	measurementSystem: types.ui.MeasurementSystem
) => String(normalizeThicknessOptionValue(value, measurementSystem));

export const resolveThicknessOptions = (
	measurementSystem: types.ui.MeasurementSystem,
	options?: ThicknessOption[]
): ThicknessOption[] => {
	const merged = new Map<string, ThicknessOption>();

	for (const option of options ?? []) {
		const key = getThicknessOptionKey(option.value, measurementSystem);
		if (!merged.has(key)) {
			merged.set(key, option);
		}
	}

	return [...merged.values()].sort(
		(left, right) =>
			normalizeThicknessOptionValue(left.value, measurementSystem) -
			normalizeThicknessOptionValue(right.value, measurementSystem)
	);
};

export const matchStandardThickness = (
	thickness: number | null | undefined,
	measurementSystem: types.ui.MeasurementSystem,
	cmu?: boolean
): number | null => {
	if (typeof thickness !== 'number' || !Number.isFinite(thickness)) {
		return null;
	}

	const normalized = normalizeThicknessOptionValue(thickness, measurementSystem);

	return getThicknessValues(measurementSystem, cmu).some(
		(value) => normalizeThicknessOptionValue(value, measurementSystem) === normalized
	)
		? normalized
		: null;
};

export type StandardThicknessConstructionType = 'timber' | 'cmu' | 'shared' | null;

/**
 * Classify a normalized thickness against both construction catalogues.
 * Non-standard values are construction-neutral, while a future value present
 * in both catalogues is explicitly shared and should preserve the frame type.
 */
export const getStandardThicknessConstructionType = (
	thickness: number | null | undefined,
	measurementSystem: types.ui.MeasurementSystem
): StandardThicknessConstructionType => {
	const timber = matchStandardThickness(thickness, measurementSystem, false) !== null;
	const cmu = matchStandardThickness(thickness, measurementSystem, true) !== null;

	if (timber && cmu) return 'shared';
	if (timber) return 'timber';
	if (cmu) return 'cmu';
	return null;
};

export const matchStandardThicknessFromRaw = (
	thickness: number | null | undefined,
	pageScale: number | null | undefined,
	measurementSystem: types.ui.MeasurementSystem,
	cmu?: boolean
): number | null =>
	matchStandardThickness(
		normalizeHitlv2ThicknessFromPageScale(thickness, pageScale),
		measurementSystem,
		cmu
	);

/**
 * Resolve an arbitrary thickness value to its normalized job-unit form for use
 * as a dropdown option value. Unlike `matchStandardThickness`, this accepts any
 * finite positive value (not just standard options).
 *
 * Returns null for missing, non-finite, zero, or negative values.
 * Metric: rounds to nearest integer mm. Imperial: rounds to nearest 1/16 inch.
 */
export const resolveThicknessOptionValue = (
	thickness: number | null | undefined,
	measurementSystem: types.ui.MeasurementSystem
): number | null => {
	if (typeof thickness !== 'number' || !Number.isFinite(thickness) || thickness <= 0) {
		return null;
	}

	const normalized = normalizeThicknessOptionValue(thickness, measurementSystem);
	return normalized > 0 ? normalized : null;
};

/**
 * Resolve an arbitrary raw (pixel-space) thickness to a normalized job-unit
 * value, converting via the page scale first. Mirrors
 * `matchStandardThicknessFromRaw` but accepts any finite positive value.
 */
export const resolveThicknessOptionValueFromRaw = (
	thickness: number | null | undefined,
	pageScale: number | null | undefined,
	measurementSystem: types.ui.MeasurementSystem
): number | null =>
	resolveThicknessOptionValue(
		normalizeHitlv2ThicknessFromPageScale(thickness, pageScale),
		measurementSystem
	);

/**
 * Generic membership check for option pickers: does `value` already exist in
 * `existingValues` once both are reduced to a comparison key? Thickness and
 * height option lists dedupe identically; pass the domain-specific key resolver.
 */
export const hasExistingOptionByKey = (
	value: number,
	existingValues: readonly number[],
	getKey: (candidate: number) => string
): boolean => {
	const key = getKey(value);
	return existingValues.some((existingValue) => getKey(existingValue) === key);
};

/**
 * Check whether a thickness value already exists in a list, comparing by
 * normalized value for the active measurement system.
 */
export const hasExistingThicknessOption = (
	value: number,
	existingValues: readonly number[],
	measurementSystem: types.ui.MeasurementSystem
): boolean =>
	hasExistingOptionByKey(value, existingValues, (candidate) =>
		getThicknessOptionKey(candidate, measurementSystem)
	);

/**
 * Convert an arbitrary job-unit thickness back to pixel-space using the given
 * page scale. Used at UI input boundaries before writing to pixel-native state.
 */
export const convertHitlv2JobUnitThicknessToPixels = (
	thickness: number | null | undefined,
	pageScale: number | null | undefined
): number | null | undefined => {
	if (typeof thickness !== 'number' || !Number.isFinite(thickness)) {
		return thickness;
	}

	if (typeof pageScale !== 'number' || !Number.isFinite(pageScale) || pageScale <= 0) {
		return thickness;
	}

	return thickness / pageScale;
};

export const getDefaultHitlv2SegmentThicknessPixels = (
	measurementSystem: types.ui.MeasurementSystem,
	pageScale: number | null | undefined,
	cmu?: boolean
): number => {
	const thickness = getDefaultSegmentThickness(measurementSystem, cmu);
	return convertHitlv2JobUnitThicknessToPixels(thickness, pageScale) ?? thickness;
};

export const formatHitlv2NativeDistance = (
	value: number,
	measurementSystem: types.ui.MeasurementSystem
): string =>
	conversionUtils.getFormattedRealDistance(
		measurementSystem === 'imperial' ? value / 12 : value,
		1,
		measurementSystem,
		16
	);

export const getStandardThicknessOptions = (
	measurementSystem: types.ui.MeasurementSystem,
	cmu?: boolean
): ThicknessOption[] =>
	getThicknessValues(measurementSystem, cmu).map((thickness) => ({
		label: formatHitlv2NativeDistance(thickness, measurementSystem),
		value: thickness,
	}));

const HITLV2_SEGMENT_RUN_EPSILON_PX = 1e-6;
const TRANSPORT_COORDINATE_DECIMALS = 8;

const roundTransportCoordinate = (value: number): number =>
	Number(value.toFixed(TRANSPORT_COORDINATE_DECIMALS));

export const areHITLV2GeometryPointsNormalized = (points: types.ui.geometry.Point[]) =>
	points.every((point) => point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1);

export const canonicalizeHITLV2PointsToPageSpace = (
	points: types.ui.geometry.Point[],
	page: { width: number; height: number }
): types.ui.geometry.Point[] =>
	areHITLV2GeometryPointsNormalized(points)
		? points.map((point) => ({
				x: point.x * page.width,
				y: point.y * page.height,
			}))
		: points;

type PersistedHITLV2CenterLineGeometry = {
	correctedCenterLine?: types.ui.geometry.Line;
	centerLine?: types.ui.geometry.Line;
};

const resolvePersistedHITLV2CenterLine = (
	geometry: PersistedHITLV2CenterLineGeometry
): types.ui.geometry.Line => {
	const centerLine = geometry.correctedCenterLine ?? geometry.centerLine;
	if (!centerLine) {
		throw new Error('HITLv2 geometry is missing correctedCenterLine');
	}
	return centerLine;
};

const resolvePersistedHITLV2Points = (
	points: types.ui.geometry.Point[] | undefined
): types.ui.geometry.Point[] => (Array.isArray(points) ? points : []);

export const derivePersistedHITLV2FrameCenterLine = (
	frame: PersistedHITLV2CenterLineGeometry & {
		correctedPoints?: types.ui.geometry.Point[];
	}
): types.ui.geometry.Line | undefined => {
	const centerLine = frame.correctedCenterLine ?? frame.centerLine;
	const derivedCenterLine = deriveHITLV2PolygonCenterLine(
		frame.correctedPoints ?? [],
		centerLine
	);
	return derivedCenterLine.length === 2
		? (derivedCenterLine as types.ui.geometry.Line)
		: undefined;
};

const resolvePersistedHITLV2FrameCenterLine = (
	frame: Parameters<typeof derivePersistedHITLV2FrameCenterLine>[0]
): types.ui.geometry.Line => {
	const centerLine = derivePersistedHITLV2FrameCenterLine(frame);
	if (!centerLine) {
		throw new Error('HITLv2 frame geometry is missing correctedCenterLine');
	}
	return centerLine;
};

export const normalizeHITLV2PointsForTransport = (
	points: types.ui.geometry.Point[],
	page: { width: number; height: number },
	options: { preserveNormalizedPoints?: boolean } = {}
): types.ui.geometry.Point[] => {
	const preserveNormalizedPoints = options.preserveNormalizedPoints ?? true;

	if (preserveNormalizedPoints && areHITLV2GeometryPointsNormalized(points)) {
		return points.map((point) => ({
			x: roundTransportCoordinate(point.x),
			y: roundTransportCoordinate(point.y),
		}));
	}

	return points.map((point) => ({
		x: roundTransportCoordinate(point.x / page.width),
		y: roundTransportCoordinate(point.y / page.height),
	}));
};

export const calculateHITLV2PolygonFromCenterLine = (
	start: types.ui.geometry.Point,
	end: types.ui.geometry.Point,
	thickness: number
): types.ui.geometry.Point[] => {
	const dx = end.x - start.x;
	const dy = end.y - start.y;
	const length = geometryUtils.calculateLineLength(start, end);

	if (length === 0) return [start, start, start, start];

	const halfThickness = thickness / 2;
	const perpX = (-dy / length) * halfThickness;
	const perpY = (dx / length) * halfThickness;

	return [
		{ x: start.x + perpX, y: start.y + perpY },
		{ x: end.x + perpX, y: end.y + perpY },
		{ x: end.x - perpX, y: end.y - perpY },
		{ x: start.x - perpX, y: start.y - perpY },
	];
};

export const updateHITLV2SegmentThicknessGeometry = (
	segment: types.job.hitlv2.frame.Segment,
	thickness: number
): types.job.hitlv2.frame.Segment => {
	const [start, end] = segment.correctedCenterLine;
	if (!start || !end) return { ...segment, thickness };

	return {
		...segment,
		thickness,
		points: calculateHITLV2PolygonFromCenterLine(start, end, thickness),
	};
};

const isUsableThickness = (thickness: number | undefined): thickness is number =>
	typeof thickness === 'number' && Number.isFinite(thickness) && thickness > 0;

/**
 * Rederive the frame's corrected outline polygon (`correctedPoints`) as a
 * naive corrected-centerline rect. Every writer that changes a frame's axis or
 * thickness must leave `correctedPoints` consistent with them; this rect is
 * the SEED/FALLBACK for frames without a junction-aware ring (new/split
 * frames, degenerate outlines) — writers with a usable ring maintain it in
 * place instead (translate, thickness scale, ring split, endpoint resize),
 * and the transport boundary preserves an existing ring rather than calling
 * this. `points` stays brain-engine-owned and is never rebuilt locally.
 * Falls back to the first segment with a usable thickness; returns the frame
 * unchanged when no endpoints or usable thickness exist.
 */
export const rebuildHITLV2FrameCorrectedOutline = (
	frame: types.job.hitlv2.frame.Type
): types.job.hitlv2.frame.Type => {
	const [frameStart, frameEnd] = frame.correctedCenterLine;
	if (!frameStart || !frameEnd) return frame;

	const thickness = isUsableThickness(frame.thickness)
		? frame.thickness
		: frame.segments.find((segment) => isUsableThickness(segment.thickness))?.thickness;
	if (thickness === undefined) return frame;

	return {
		...frame,
		correctedPoints: calculateHITLV2PolygonFromCenterLine(frameStart, frameEnd, thickness),
	};
};

/**
 * Width-scale the frame's existing corrected outline about its corrected axis
 * by `newThickness / frame.thickness`, preserving the along-axis (length)
 * extent. This keeps a junction-aware polygon's shape through a thickness-only
 * edit instead of snapping it back to a naive axis-aligned rectangle. Returns
 * `undefined` (caller should naive-rebuild) when there is nothing meaningful to
 * scale: no usable corrected axis, fewer than 3 corrected points, or an unusable
 * old/new thickness.
 */
export const scaleHITLV2FrameCorrectedOutlineThickness = (
	frame: types.job.hitlv2.frame.Type,
	newThickness: number
): types.ui.geometry.Point[] | undefined => {
	const existing = frame.correctedPoints;
	const [start, end] = frame.correctedCenterLine;
	const oldThickness = frame.thickness;

	if (
		!existing ||
		existing.length < 3 ||
		!start ||
		!end ||
		!isUsableThickness(oldThickness) ||
		!isUsableThickness(newThickness)
	) {
		return undefined;
	}

	const axisLength = geometryUtils.calculateLineLength(start, end);
	if (axisLength === 0) return undefined;

	const axisX = (end.x - start.x) / axisLength;
	const axisY = (end.y - start.y) / axisLength;
	const ratio = newThickness / oldThickness;

	return existing.map((point) => {
		const dx = point.x - start.x;
		const dy = point.y - start.y;
		// Component along the axis is preserved; the perpendicular remainder scales.
		const along = dx * axisX + dy * axisY;
		const alongX = along * axisX;
		const alongY = along * axisY;
		const perpX = dx - alongX;
		const perpY = dy - alongY;

		return {
			x: start.x + alongX + perpX * ratio,
			y: start.y + alongY + perpY * ratio,
		};
	});
};

export const updateHITLV2FrameAndSegmentsThickness = (
	frame: types.job.hitlv2.frame.Type,
	thickness: number,
	segmentThicknessById?: ReadonlyMap<string, number>
): types.job.hitlv2.frame.Type => {
	// Compute the scaled outline from the original frame, before `thickness` is
	// overwritten — the scale ratio needs the old thickness.
	const scaledCorrectedPoints = scaleHITLV2FrameCorrectedOutlineThickness(frame, thickness);

	const next: types.job.hitlv2.frame.Type = {
		...frame,
		thickness,
		segments: frame.segments.map((segment) =>
			updateHITLV2SegmentThicknessGeometry(
				segment,
				segmentThicknessById?.get(segment.id) ?? thickness
			)
		),
	};

	// Bias to the junction-aware polygon: scale it when we can, else fall back to
	// a naive corrected-axis rectangle.
	return scaledCorrectedPoints
		? { ...next, correctedPoints: scaledCorrectedPoints }
		: rebuildHITLV2FrameCorrectedOutline(next);
};

export const updateHITLV2FrameAndSegmentsHeight = (
	frame: types.job.hitlv2.frame.Type,
	height: number,
	segmentHeightById?: ReadonlyMap<string, number>
): types.job.hitlv2.frame.Type => ({
	...frame,
	height,
	segments: frame.segments.map((segment) => ({
		...segment,
		height: segmentHeightById?.get(segment.id) ?? height,
	})),
});

const ensureHITLV2SegmentPoints = (
	segment: types.job.hitlv2.frame.Segment
): types.job.hitlv2.frame.Segment => {
	if (segment.points.length > 0) return segment;

	const [start, end] = segment.correctedCenterLine;
	if (!start || !end) return segment;

	return updateHITLV2SegmentThicknessGeometry(segment, segment.thickness);
};

const getPointAtDistance = (
	origin: types.ui.geometry.Point,
	unit: types.ui.geometry.Point,
	distance: number
): types.ui.geometry.Point => ({
	x: origin.x + unit.x * distance,
	y: origin.y + unit.y * distance,
});

const projectPointDistance = (
	point: types.ui.geometry.Point,
	origin: types.ui.geometry.Point,
	unit: types.ui.geometry.Point
): number => (point.x - origin.x) * unit.x + (point.y - origin.y) * unit.y;

export const getHITLV2FrameExtentFromChildren = (
	segments: types.job.hitlv2.frame.Segment[],
	openings: types.job.hitlv2.frame.Opening[],
	fallbackCenterLine: [types.ui.geometry.Point, types.ui.geometry.Point]
): [types.ui.geometry.Point, types.ui.geometry.Point] => {
	const [fallbackStart, fallbackEnd] = fallbackCenterLine;
	const dx = fallbackEnd.x - fallbackStart.x;
	const dy = fallbackEnd.y - fallbackStart.y;
	const length = geometryUtils.calculateLineLength(fallbackStart, fallbackEnd);
	if (length <= HITLV2_SEGMENT_RUN_EPSILON_PX) return fallbackCenterLine;

	const unit = {
		x: dx / length,
		y: dy / length,
	};

	const childPoints = [
		...segments.flatMap((segment) => segment.correctedCenterLine),
		...openings.flatMap((opening) => opening.correctedCenterLine),
	].filter((point): point is types.ui.geometry.Point => point !== undefined);

	if (childPoints.length === 0) return fallbackCenterLine;

	const distances = childPoints.map((point) => projectPointDistance(point, fallbackStart, unit));
	const minDistance = Math.min(...distances);
	const maxDistance = Math.max(...distances);

	return [
		getPointAtDistance(fallbackStart, unit, minDistance),
		getPointAtDistance(fallbackStart, unit, maxDistance),
	];
};

export const deriveHITLV2PolygonCenterLine = (
	ring: types.ui.geometry.Point[],
	centerLine?: types.ui.geometry.Line
): types.ui.geometry.Point[] => {
	const [start, end] = centerLine ?? [];
	if (start && end) {
		const length = geometryUtils.calculateLineLength(start, end);
		if (length <= HITLV2_SEGMENT_RUN_EPSILON_PX || ring.length < 3) {
			return centerLine ?? [];
		}

		const unit = {
			x: (end.x - start.x) / length,
			y: (end.y - start.y) / length,
		};
		const distances = ring.map((point) => projectPointDistance(point, start, unit));

		return [
			getPointAtDistance(start, unit, Math.min(...distances)),
			getPointAtDistance(start, unit, Math.max(...distances)),
		];
	}

	if (ring.length < 3) return [];

	let longestEdge:
		| {
				start: types.ui.geometry.Point;
				end: types.ui.geometry.Point;
				length: number;
		  }
		| undefined;
	for (let index = 0; index < ring.length; index += 1) {
		const edgeStart = ring[index];
		const edgeEnd = ring[(index + 1) % ring.length];
		if (!edgeStart || !edgeEnd) continue;
		const edgeLength = geometryUtils.calculateLineLength(edgeStart, edgeEnd);
		if (!longestEdge || edgeLength > longestEdge.length) {
			longestEdge = { start: edgeStart, end: edgeEnd, length: edgeLength };
		}
	}
	if (!longestEdge || longestEdge.length <= HITLV2_SEGMENT_RUN_EPSILON_PX) return [];

	const unit = {
		x: (longestEdge.end.x - longestEdge.start.x) / longestEdge.length,
		y: (longestEdge.end.y - longestEdge.start.y) / longestEdge.length,
	};
	const perpendicular = { x: -unit.y, y: unit.x };
	const alongDistances = ring.map((point) => point.x * unit.x + point.y * unit.y);
	const perpendicularDistances = ring.map(
		(point) => point.x * perpendicular.x + point.y * perpendicular.y
	);
	const middlePerpendicular =
		(Math.min(...perpendicularDistances) + Math.max(...perpendicularDistances)) / 2;

	return [Math.min(...alongDistances), Math.max(...alongDistances)].map((distance) => ({
		x: unit.x * distance + perpendicular.x * middlePerpendicular,
		y: unit.y * distance + perpendicular.y * middlePerpendicular,
	}));
};

/**
 * Junction-aware ("end to end") centreline: the extent of `correctedPoints`
 * projected onto the authoritative corrected axis.
 */
export const deriveHITLV2FrameCorrectedCenterLine = (
	frame: Omit<types.job.hitlv2.frame.Type, 'correctedCenterLine'> & {
		correctedCenterLine?: types.ui.geometry.Line;
	}
): types.ui.geometry.Point[] =>
	deriveHITLV2PolygonCenterLine(frame.correctedPoints ?? [], frame.correctedCenterLine);

export const rebuildHITLV2FrameGeometryFromSegments = (
	frame: types.job.hitlv2.frame.Type
): types.job.hitlv2.frame.Type => {
	if (frame.segments.length === 0) {
		return frame;
	}

	const segments = frame.segments.map(ensureHITLV2SegmentPoints);
	const firstSegment = segments[0];
	const lastSegment = segments[segments.length - 1];
	if (!firstSegment || !lastSegment) return frame;

	const firstPoint = firstSegment.correctedCenterLine[0];
	const lastPoint = lastSegment.correctedCenterLine[1];
	if (!firstPoint || !lastPoint) return frame;

	const correctedCenterLine = getHITLV2FrameExtentFromChildren(segments, frame.openings, [
		firstPoint,
		lastPoint,
	]);

	// The junction-aware corrected outline is maintained in place by every
	// local writer (translate, thickness scale, ring split, endpoint resize),
	// so an existing ring is preserved here. Rebuilding a naive rectangle
	// would clobber brain-engine junction trims on every frame the commit
	// carries. The rect rebuild is only the seed for frames without a ring.
	const hasUsableRing = (frame.correctedPoints?.length ?? 0) >= 3;
	const next = {
		...frame,
		correctedCenterLine,
		segments,
	};
	const rebuilt = hasUsableRing ? next : rebuildHITLV2FrameCorrectedOutline(next);
	return rebuilt;
};

export const canonicalizeHITLV2FrameGeometryToPageSpace = (
	frame: types.job.hitlv2.frame.Type,
	page: { width: number; height: number }
): types.job.hitlv2.frame.Type => ({
	...frame,
	...(frame.correctedPoints
		? { correctedPoints: canonicalizeHITLV2PointsToPageSpace(frame.correctedPoints, page) }
		: {}),
	correctedCenterLine: canonicalizeHITLV2PointsToPageSpace(
		resolvePersistedHITLV2FrameCenterLine(frame),
		page
	),
	segments: frame.segments.map((segment) => ({
		...segment,
		points: canonicalizeHITLV2PointsToPageSpace(
			resolvePersistedHITLV2Points(segment.points),
			page
		),
		correctedCenterLine: canonicalizeHITLV2PointsToPageSpace(
			resolvePersistedHITLV2CenterLine(segment),
			page
		),
	})),
	openings: frame.openings.map((opening) => ({
		...opening,
		correctedCenterLine: canonicalizeHITLV2PointsToPageSpace(
			resolvePersistedHITLV2CenterLine(opening),
			page
		),
	})),
});

export const serializeHITLV2FrameGeometryForTransport = (
	frame: types.job.hitlv2.frame.Type,
	page: { width: number; height: number },
	options: { preserveNormalizedPoints?: boolean; rebuildFrameGeometry?: boolean } = {}
): types.job.hitlv2.frame.Type => {
	const frameForTransport = options.rebuildFrameGeometry
		? rebuildHITLV2FrameGeometryFromSegments(frame)
		: frame;

	return {
		...frameForTransport,
		...(frameForTransport.correctedPoints
			? {
					correctedPoints: normalizeHITLV2PointsForTransport(
						frameForTransport.correctedPoints,
						page,
						options
					),
				}
			: {}),
		correctedCenterLine: normalizeHITLV2PointsForTransport(
			frameForTransport.correctedCenterLine,
			page,
			options
		),
		segments: frameForTransport.segments.map((segment) => ({
			...segment,
			points: normalizeHITLV2PointsForTransport(
				resolvePersistedHITLV2Points(segment.points),
				page,
				options
			),
			correctedCenterLine: normalizeHITLV2PointsForTransport(
				segment.correctedCenterLine,
				page,
				options
			),
		})),
		openings: frameForTransport.openings.map((opening) => ({
			...opening,
			correctedCenterLine: normalizeHITLV2PointsForTransport(
				opening.correctedCenterLine,
				page,
				options
			),
		})),
	};
};

export const canonicalizeHITLV2ChangeToPageSpace = (
	change: types.job.hitlv2.change.Type,
	page: { width: number; height: number }
): types.job.hitlv2.change.Type => {
	switch (change.type) {
		case 'frame-created':
		case 'frame-updated':
			return {
				...change,
				frame: canonicalizeHITLV2FrameGeometryToPageSpace(change.frame, page),
			};

		case 'segment-created':
		case 'segment-updated':
			return {
				...change,
				segment: {
					...change.segment,
					points: canonicalizeHITLV2PointsToPageSpace(
						resolvePersistedHITLV2Points(change.segment.points),
						page
					),
					correctedCenterLine: canonicalizeHITLV2PointsToPageSpace(
						resolvePersistedHITLV2CenterLine(change.segment),
						page
					),
				},
			};

		case 'opening-created':
		case 'opening-updated':
			return {
				...change,
				opening: {
					...change.opening,
					correctedCenterLine: canonicalizeHITLV2PointsToPageSpace(
						resolvePersistedHITLV2CenterLine(change.opening),
						page
					),
				},
			};

		case 'frames-snapshot':
			return {
				...change,
				frames: change.frames.map((frame) =>
					canonicalizeHITLV2FrameGeometryToPageSpace(frame, page)
				),
			};

		default:
			return change;
	}
};

export const serializeHITLV2ChangeForTransport = (
	change: types.job.hitlv2.change.Type,
	page: { width: number; height: number },
	options: { preserveNormalizedPoints?: boolean; rebuildFrameGeometry?: boolean } = {}
): types.job.hitlv2.change.Type => {
	switch (change.type) {
		case 'frame-created':
		case 'frame-updated':
			return {
				...change,
				frame: serializeHITLV2FrameGeometryForTransport(change.frame, page, {
					rebuildFrameGeometry: true,
					...options,
				}),
			};

		case 'segment-created':
		case 'segment-updated':
			return {
				...change,
				segment: {
					...change.segment,
					points: normalizeHITLV2PointsForTransport(
						resolvePersistedHITLV2Points(change.segment.points),
						page,
						options
					),
					correctedCenterLine: normalizeHITLV2PointsForTransport(
						change.segment.correctedCenterLine,
						page,
						options
					),
				},
			};

		case 'opening-created':
		case 'opening-updated':
			return {
				...change,
				opening: {
					...change.opening,
					correctedCenterLine: normalizeHITLV2PointsForTransport(
						change.opening.correctedCenterLine,
						page,
						options
					),
				},
			};

		case 'frames-snapshot':
			return {
				...change,
				frames: change.frames.map((frame) =>
					serializeHITLV2FrameGeometryForTransport(frame, page, {
						rebuildFrameGeometry: true,
						...options,
					})
				),
			};

		default:
			return change;
	}
};
